import React, { useState } from "react";
import IntegrationDataMapping from "../shared/IntegrationDataMapping";
import { SectionCard } from "@/components/ds";

// ---------------------------------------------------------------------------
// Guide sub-components
// ---------------------------------------------------------------------------

type GuideSection = "overview" | "agent-panel" | "workflow-data" | "ai-tools";

const GUIDE_SECTIONS: { id: GuideSection; title: string }[] = [
  { id: "overview", title: "What is field mapping?" },
  { id: "agent-panel", title: "Agent CRM panel" },
  { id: "workflow-data", title: "HTTP POST / workflows" },
  { id: "ai-tools", title: "AI tools (no mapping)" }
];

function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="bg-zinc-900 text-zinc-100 rounded-lg p-3 text-xs font-mono overflow-x-auto whitespace-pre leading-relaxed mt-2">
      {children}
    </pre>
  );
}

function InfoBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-primary-bg border border-cornflower-blue-200 rounded-lg p-3 mt-3 text-xs text-cornflower-blue-800 leading-relaxed">
      {children}
    </div>
  );
}

function TermBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-zinc-50 border border-zinc-200 rounded p-3 mt-3 text-xs text-zinc-600 leading-relaxed">
      {children}
    </div>
  );
}

function InlineCode({ children }: { children: string }) {
  return (
    <code className="bg-white px-1 py-0.5 rounded border border-zinc-200 text-cornflower-blue-800 font-mono text-[11px]">
      {children}
    </code>
  );
}

function GuideOverview() {
  return (
    <div>
      <h3 className="font-semibold text-zinc-800 mb-2">
        What is Field Mapping?
      </h3>
      <p className="mb-3">
        Field mapping tells the platform{" "}
        <strong>which Mindbody data to surface in the agent CRM sidebar</strong>{" "}
        and what to call it. When an agent opens a conversation, the sidebar
        shows client and class information pulled from Mindbody — field mapping
        controls exactly which fields appear and their display labels.
      </p>
      <p className="mb-3">
        Think of it as a <em>view configuration</em>: you pick a Mindbody object
        type (Class, Client, Service, Staff), choose the fields you care about,
        give them friendly labels, and decide whether agents can search on them.
      </p>

      <h4 className="font-medium text-zinc-700 mt-4 mb-2">
        Quick decision guide
      </h4>
      <div className="border border-zinc-200 rounded-lg overflow-hidden text-xs">
        <table className="w-full">
          <thead className="bg-zinc-100 text-zinc-600">
            <tr>
              <th className="px-3 py-2 text-left">Use case</th>
              <th className="px-3 py-2 text-left">Field mapping needed?</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            <tr>
              <td className="px-3 py-2">
                Show client data in the agent sidebar
              </td>
              <td className="px-3 py-2 text-success-green-700 font-medium">
                Yes — required
              </td>
            </tr>
            <tr className="bg-zinc-50">
              <td className="px-3 py-2">
                Pass Mindbody data into an HTTP POST workflow node
              </td>
              <td className="px-3 py-2 text-success-green-700 font-medium">
                Yes — required
              </td>
            </tr>
            <tr>
              <td className="px-3 py-2">
                AI answers class schedule or booking questions
              </td>
              <td className="px-3 py-2 text-zinc-500 font-medium">
                No — AI reads raw data
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <TermBox>
        <strong>Scope type</strong> — Mindbody data is grouped into object
        types: Class, Client, Service, Staff. Each mapping panel targets one
        scope type. You can add multiple panels to map fields from different
        object types.
      </TermBox>
    </div>
  );
}

function GuideAgentPanel() {
  return (
    <div>
      <h3 className="font-semibold text-zinc-800 mb-2">Agent CRM Panel</h3>
      <p className="mb-3">
        When an agent handles a conversation, the right-side CRM panel displays
        a client's linked Mindbody data. The fields shown, their order, and
        their labels all come from this mapping configuration.
      </p>

      <h4 className="font-medium text-zinc-700 mt-4 mb-1">
        Field options explained
      </h4>
      <div className="border border-zinc-200 rounded-lg overflow-hidden text-xs">
        <table className="w-full">
          <thead className="bg-zinc-100 text-zinc-600">
            <tr>
              <th className="px-3 py-2 text-left">Option</th>
              <th className="px-3 py-2 text-left">Effect</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            <tr>
              <td className="px-3 py-2 font-medium">Label</td>
              <td className="px-3 py-2">
                The display name agents see. Rename{" "}
                <InlineCode>membership_name</InlineCode> to "Membership".
              </td>
            </tr>
            <tr className="bg-zinc-50">
              <td className="px-3 py-2 font-medium">Show in search</td>
              <td className="px-3 py-2">
                Agents can search conversations by this field value (e.g. search
                by membership type).
              </td>
            </tr>
            <tr>
              <td className="px-3 py-2 font-medium">Show when selected</td>
              <td className="px-3 py-2">
                Field appears in the contact detail view when the agent drills
                into a record.
              </td>
            </tr>
            <tr className="bg-zinc-50">
              <td className="px-3 py-2 font-medium">Default value</td>
              <td className="px-3 py-2">
                Fallback text when the Mindbody field is empty or null.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <InfoBox>
        <strong>Tip:</strong> Map only the fields your agents actually need to
        take action. A focused card with membership status, home location, and
        next class is more useful than all available fields.
      </InfoBox>

      <h4 className="font-medium text-zinc-700 mt-4 mb-1">
        Mindbody object reference
      </h4>
      <CodeBlock>{`Class:   id, class_name, instructor_name, start_date_time, end_date_time,
         location_name, spots_available, web_description
Client:  id, first_name, last_name, email, phone, home_location,
         status, membership_name
Service: id, name, description, price, duration_mins, category
Staff:   id, first_name, last_name, email, bio, image_url`}</CodeBlock>

      <h4 className="font-medium text-zinc-700 mt-4 mb-1">
        Example: Client mapping
      </h4>
      <CodeBlock>{`Mindbody Field    Label              Search  Show
──────────────────────────────────────────────────
email             Email              ✓       ✓
first_name        First Name         ✓       ✓
last_name         Last Name          ✓       ✓
membership_name   Membership                 ✓
home_location     Home Studio                ✓
status            Status                     ✓`}</CodeBlock>
    </div>
  );
}

function GuideWorkflowData() {
  return (
    <div>
      <h3 className="font-semibold text-zinc-800 mb-2">
        HTTP POST / Workflow Data Passing
      </h3>
      <p className="mb-3">
        In a workflow, an <strong>HTTP POST node</strong> can call an external
        API and include Mindbody data in the request body. To pass a Mindbody
        field value, you first map it — that gives it a variable name — then
        reference that variable in the POST node.
      </p>

      <h4 className="font-medium text-zinc-700 mt-4 mb-1">
        How the variable is formed
      </h4>
      <p className="text-xs text-zinc-600 mb-2">
        Once you map a Mindbody field, it becomes available as a workflow
        variable. The variable name follows the pattern{" "}
        <InlineCode>{"{mindbody.scopeType.fieldName}"}</InlineCode>:
      </p>
      <CodeBlock>{`Scope: Client  →  Field: membership_name
Variable: {mindbody.client.membership_name}

Scope: Class   →  Field: start_date_time
Variable: {mindbody.class.start_date_time}`}</CodeBlock>

      <h4 className="font-medium text-zinc-700 mt-4 mb-1">
        Example: Send booking confirmation
      </h4>
      <CodeBlock>{`// HTTP POST node — request body
{
  "client_email":    "{mindbody.client.email}",
  "class_name":      "{mindbody.class.class_name}",
  "instructor":      "{mindbody.class.instructor_name}",
  "start_time":      "{mindbody.class.start_date_time}",
  "location":        "{mindbody.class.location_name}",
  "conversation":    "{conversation.id}"
}`}</CodeBlock>

      <InfoBox>
        <strong>Field mapping is the bridge.</strong> Without a mapping entry
        for <InlineCode>class.start_date_time</InlineCode>, the variable{" "}
        <InlineCode>{"{mindbody.class.start_date_time}"}</InlineCode> would be
        empty at runtime. Map first, then reference in the POST node.
      </InfoBox>
    </div>
  );
}

function GuideAiTools() {
  return (
    <div>
      <h3 className="font-semibold text-zinc-800 mb-2">
        AI Tools — No Field Mapping Required
      </h3>
      <p className="mb-3">
        Mindbody AI tools work without field mapping — they display class
        schedules, book appointments, look up clients, and list services
        automatically. Field mapping configures what appears in the agent
        sidebar.
      </p>

      <h4 className="font-medium text-zinc-700 mt-3 mb-1">How AI tools work</h4>
      <p className="text-xs text-zinc-600 mb-2">
        When an AI workflow node runs, it calls the Mindbody API at runtime and
        receives the full raw JSON response. The AI model extracts relevant
        details and composes a natural language response — no schema
        configuration needed.
      </p>
      <CodeBlock>{`// What the AI receives internally
{
  "classes": [
    {
      "class_name": "Vinyasa Flow",
      "instructor_name": "Sarah Chen",
      "start_date_time": "2026-03-25T09:00:00",
      "location_name": "Downtown Studio",
      "spots_available": 4
    }
  ]
}

// What the AI says to the visitor
"There's a Vinyasa Flow class Tuesday at 9:00 AM with
 Sarah Chen at Downtown Studio — 4 spots left.
 Would you like me to book you in?"`}</CodeBlock>

      <InfoBox>
        <strong>Why no mapping?</strong> Field mapping is a{" "}
        <em>static display config</em> — you define once what to show in the
        sidebar. AI tools are <em>dynamic runtime calls</em> — the AI decides
        which fields matter based on the visitor's question.
      </InfoBox>

      <h4 className="font-medium text-zinc-700 mt-4 mb-1">
        Comparison: AI tool vs mapped field
      </h4>
      <div className="border border-zinc-200 rounded-lg overflow-hidden text-xs mt-2">
        <table className="w-full">
          <thead className="bg-zinc-100 text-zinc-600">
            <tr>
              <th className="px-3 py-2 text-left">Capability</th>
              <th className="px-3 py-2 text-left">AI Tool</th>
              <th className="px-3 py-2 text-left">Field Mapping</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 text-xs">
            <tr>
              <td className="px-3 py-2">Agent sidebar display</td>
              <td className="px-3 py-2 text-zinc-400">No</td>
              <td className="px-3 py-2 text-success-green-700">Yes</td>
            </tr>
            <tr className="bg-zinc-50">
              <td className="px-3 py-2">Visitor-facing AI answers</td>
              <td className="px-3 py-2 text-success-green-700">Yes</td>
              <td className="px-3 py-2 text-zinc-400">No</td>
            </tr>
            <tr>
              <td className="px-3 py-2">HTTP POST variable injection</td>
              <td className="px-3 py-2 text-zinc-400">No</td>
              <td className="px-3 py-2 text-success-green-700">Yes</td>
            </tr>
            <tr className="bg-zinc-50">
              <td className="px-3 py-2">Requires configuration</td>
              <td className="px-3 py-2 text-zinc-500">No (auto-activated)</td>
              <td className="px-3 py-2 text-zinc-500">Yes (this panel)</td>
            </tr>
          </tbody>
        </table>
      </div>

      <TermBox>
        <strong>When you need both:</strong> An AI workflow node can answer
        "what yoga classes are available Saturday?" without any mapping. But if
        the same workflow also sends a POST to your notification service, you
        need field mapping to supply the{" "}
        <InlineCode>{"{mindbody.client.email}"}</InlineCode> variable that goes
        into the POST body.
      </TermBox>
    </div>
  );
}

function FieldMappingGuide() {
  const [guideSection, setGuideSection] = useState<GuideSection>("overview");
  return (
    <SectionCard className="p-0 mb-4">
      <div className="flex">
        {/* Sidebar nav */}
        <nav
          className="w-48 shrink-0 border-r border-zinc-200 p-3"
          aria-label="Field mapping guide sections"
        >
          <div className="space-y-0.5">
            {GUIDE_SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => setGuideSection(s.id)}
                className={`block w-full text-left px-3 py-1.5 rounded text-xs transition-colors ${
                  guideSection === s.id
                    ? "bg-primary-bg text-primary-active font-medium"
                    : "text-zinc-600 hover:bg-zinc-100"
                }`}
              >
                {s.title}
              </button>
            ))}
          </div>
        </nav>

        {/* Content */}
        <div className="flex-1 p-5 text-sm text-zinc-700 leading-relaxed overflow-y-auto max-h-[520px]">
          {guideSection === "overview" && <GuideOverview />}
          {guideSection === "agent-panel" && <GuideAgentPanel />}
          {guideSection === "workflow-data" && <GuideWorkflowData />}
          {guideSection === "ai-tools" && <GuideAiTools />}
        </div>
      </div>
    </SectionCard>
  );
}

const SCOPE_OPTIONS = ["Class", "Client", "Service", "Staff"];

interface Props {
  teamId: number;
  selectedConfigId: number;
}

export default function MindbodyDataMapping(props: Props) {
  return (
    <IntegrationDataMapping
      selectedConfigId={props.selectedConfigId}
      teamId={props.teamId}
      description="Configure which Mindbody fields appear in the agent CRM sidebar and as workflow variables."
      guide={<FieldMappingGuide />}
      guideLabel="Field Mapping Guide ▾"
      fieldKey="MindbodyField"
      fieldLabel="Mindbody Field"
      apiPrefix="Mindbody/Integrations"
      getFieldsPath="GetMindbodyFields"
      scopeOptions={SCOPE_OPTIONS}
    />
  );
}
